// W2-10 (SCRUM-184): the owner's daily briefing.
// Contract: GET /api/v1/session/reports/daily-briefing?date=YYYY-MM-DD.
type UnknownRecord = Record<string, unknown>;

export type ConversationOrigins = {
  total: number;
  ad: number;
  organic: number;
  unknown: number;
};

export type FirstResponseSeconds = {
  p50: number | null;
  p95: number | null;
  sample: number;
};

export type BookingSplit = {
  total: number;
  by_ai: number;
  by_human: number;
};

export type HitlQueueCounts = {
  pending: number;
  approved: number;
  rejected: number;
  expired: number;
};

/**
 * SCRUM-194: what the clinic's AI cost on the day, in both currencies.
 *
 * `sources` names the runtimes the figure was built from — the conversation
 * engine and the control plane — and the object only exists when both
 * answered. `fx_rate` is the published rate the BRL figure was converted at,
 * and `fx_date` the day it was published on.
 */
export type LlmCost = {
  usd: number;
  brl: number;
  fx_rate: number | null;
  fx_date: string | null;
  sources: string[];
};

export type DailyBriefing = {
  date: string;
  timezone: string;
  conversations: ConversationOrigins;
  first_response_seconds: FirstResponseSeconds;
  bookings: BookingSplit;
  escalations_open: number;
  /** Null while the AI spend of the day is not readable as a whole. */
  llm_cost: LlmCost | null;
  hitl: HitlQueueCounts;
};

export type DailyBriefingResponse = {
  data: DailyBriefing;
  meta: { generated_at: string; unavailable: string[] };
};

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as UnknownRecord;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string") throw new Error(`${path} must be a string`);
  return value;
}

function integer(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new Error(`${path} must be an integer`);
  }
  return value;
}

function nullableNumber(value: unknown, path: string): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || Number.isNaN(value)) {
    throw new Error(`${path} must be a number or null`);
  }
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value === null || value === undefined) return null;
  return string(value, path);
}

/**
 * The day's AI cost, or null. Null is the API saying it could not read one of
 * the two runtimes, and the app writes a sentence for it rather than a zero.
 */
function decodeLlmCost(value: unknown): LlmCost | null {
  if (value === null || value === undefined) return null;
  const cost = record(value, "response.data.llm_cost");
  const sources = cost.sources;

  return {
    usd: integerOrNumber(cost.usd, "response.data.llm_cost.usd"),
    brl: integerOrNumber(cost.brl, "response.data.llm_cost.brl"),
    fx_rate: nullableNumber(cost.fx_rate, "response.data.llm_cost.fx_rate"),
    fx_date: nullableString(cost.fx_date, "response.data.llm_cost.fx_date"),
    sources: Array.isArray(sources)
      ? sources.map((item, index) =>
          string(item, `response.data.llm_cost.sources[${index}]`)
        )
      : [],
  };
}

function integerOrNumber(value: unknown, path: string): number {
  const number = nullableNumber(value, path);
  if (number === null) throw new Error(`${path} must be a number`);
  return number;
}

export function decodeDailyBriefingResponse(
  value: unknown
): DailyBriefingResponse {
  const root = record(value, "response");
  const data = record(root.data, "response.data");
  const meta = record(root.meta, "response.meta");
  const conversations = record(data.conversations, "response.data.conversations");
  const response = record(
    data.first_response_seconds,
    "response.data.first_response_seconds"
  );
  const bookings = record(data.bookings, "response.data.bookings");
  const hitl = record(data.hitl, "response.data.hitl");

  return {
    data: {
      date: string(data.date, "response.data.date"),
      timezone: string(data.timezone, "response.data.timezone"),
      conversations: {
        total: integer(conversations.total, "response.data.conversations.total"),
        ad: integer(conversations.ad, "response.data.conversations.ad"),
        organic: integer(
          conversations.organic,
          "response.data.conversations.organic"
        ),
        unknown: integer(
          conversations.unknown,
          "response.data.conversations.unknown"
        ),
      },
      first_response_seconds: {
        p50: nullableNumber(response.p50, "response.data.first_response_seconds.p50"),
        p95: nullableNumber(response.p95, "response.data.first_response_seconds.p95"),
        sample: integer(
          response.sample,
          "response.data.first_response_seconds.sample"
        ),
      },
      bookings: {
        total: integer(bookings.total, "response.data.bookings.total"),
        by_ai: integer(bookings.by_ai, "response.data.bookings.by_ai"),
        by_human: integer(bookings.by_human, "response.data.bookings.by_human"),
      },
      hitl: {
        pending: integer(hitl.pending, "response.data.hitl.pending"),
        approved: integer(hitl.approved, "response.data.hitl.approved"),
        rejected: integer(hitl.rejected, "response.data.hitl.rejected"),
        expired: integer(hitl.expired, "response.data.hitl.expired"),
      },
      escalations_open: integer(
        data.escalations_open,
        "response.data.escalations_open"
      ),
      llm_cost: decodeLlmCost(data.llm_cost),
    },
    meta: {
      generated_at: string(meta.generated_at, "response.meta.generated_at"),
      unavailable: Array.isArray(meta.unavailable)
        ? meta.unavailable.map((item, index) =>
            string(item, `response.meta.unavailable[${index}]`)
          )
        : [],
    },
  };
}

// The API path is written out at each `httpRequest` call site on purpose:
// scripts/api-calls.mjs reads string literals, and a shared constant would
// hide this route from apps/api's WebApiCallsTest.

/** Where the full briefing lives in the clinic app. */
export const DAILY_BRIEFING_ROUTE = "/relatorios/briefing";

/** `2026-09-08` → `08/09/2026`; anything else comes back unchanged. */
export function formatBriefingDate(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/** Today in the browser's calendar, as the `date` input expects it. */
export function todayInputValue(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** What the clinic reads when the day's AI cost could not be stated whole. */
export const COST_UNAVAILABLE = "Custo indisponível";

/**
 * Money in the clinic's own reading (SCRUM-194). Four decimals below one
 * cent, because a day of AI can genuinely cost R$ 0,0042 and rounding it to
 * "R$ 0,00" would read as free.
 */
export function formatMoney(value: number, currency: "BRL" | "USD"): string {
  const belowACent = value > 0 && value < 0.01;

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: belowACent ? 4 : 2,
  }).format(value);
}

/** The published FX rate, as a rate and not as a price. */
export function formatRate(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

/**
 * A duration in the clinic's words: seconds under a minute, minutes under an
 * hour, hours and minutes above it. `null` is "not measured", never "zero".
 */
export function formatSeconds(value: number | null): string {
  if (value === null) return "—";
  const seconds = Math.max(0, Math.round(value));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
